import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import useOrderWorkunitApi from './useOrderWorkunitApi';
import useOrderWorkunitsService from './useOrderWorkunitsService';

const useOrderValidation = (onRedirectToPreview?: (orderIsSubmitted: boolean) => void) => {
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const order = useSelector((state) => state.catalogToOrder.order);
  const selectedOrderWorkunits = useSelector((state) => state.catalogToOrder.selectedWorkunits);
  const isExtractLoading = useSelector((state) => state.loadings.extract_excel_summary);
  const {
    updateOrderStatus,
    bulkCreateOrderWorkunits,
    exportExcel,
    handleUpdateOrderAction,
    hasUnsavedWorkunitChanges,
  } = useOrderWorkunitApi();
  const { redirectToPreview: defaultRedirectToPreview } = useOrderWorkunitsService();
  const redirectToPreview = onRedirectToPreview ?? defaultRedirectToPreview;

  const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
  const [isReferencesModalOpen, setIsReferencesModalOpen] = useState(false);
  const [specRef, setSpecRef] = useState('');
  const [chronoRef, setChronoRef] = useState('');
  const [referencesError, setReferencesError] = useState('');

  const handleValidationOrder = async (is_submitted: boolean, is_rogue = false) => {
    try {
      if (order) {
        if (!is_rogue && is_submitted) {
          // Transition drafted quotes to pending; otherwise the order already has a
          // status, so just persist the edits made in the wizard without touching it.
          if (order.status === 'drafted') {
            await updateOrderStatus(order);
          } else {
            await handleUpdateOrderAction(order);
          }
        }

        if (selectedOrderWorkunits.length) {
          await bulkCreateOrderWorkunits();
        }

        if (is_submitted && !is_rogue) {
          setIsExtractModalOpen(true);
        }
      }
    } catch (e) {
      const result = (e as Error).message;
      dispatch(
        setSnackbarAction({
          message: result
            ? `error while saving order with workunits: ${result}`
            : `error while saving order with workunits :${e}`,
          open: true,
          severity: 'error',
        })
      );
    }
  };

  const handleExtractModalClose = () => {
    setIsExtractModalOpen(false);
  };

  const handleOpenReferencesModal = () => {
    setIsExtractModalOpen(false);
    setIsReferencesModalOpen(true);
    setReferencesError('');
  };

  const handleReferencesModalClose = () => {
    setIsReferencesModalOpen(false);
    setSpecRef('');
    setChronoRef('');
    setReferencesError('');
  };

  const downloadExtractAndRedirectWithReferences = async () => {
    if (!specRef.trim() || !chronoRef.trim()) {
      setReferencesError(t('Both reference fields are required'));
      return;
    }

    try {
      if (order) {
        await exportExcel(specRef.trim(), chronoRef.trim());

        setIsReferencesModalOpen(false);
        setSpecRef('');
        setChronoRef('');
        redirectToPreview(order?.status === 'pending');
      }
    } catch (error) {
      setReferencesError(t('Error occurred while extracting Excel file'));
    }
  };

  return {
    order,
    isExtractModalOpen,
    isReferencesModalOpen,
    isExtractLoading,
    specRef,
    setSpecRef,
    chronoRef,
    setChronoRef,
    referencesError,
    handleValidationOrder,
    handleExtractModalClose,
    handleOpenReferencesModal,
    handleReferencesModalClose,
    downloadExtractAndRedirectWithReferences,
    redirectToPreview,
    hasUnsavedWorkunitChanges,
  };
};

export default useOrderValidation;
